import { Injectable } from '@angular/core';
import { ExportContext, ExportFileFormat, ExportRange, ExportService, ExportTaskProcessor } from 'app/layout/export-overlay';
import { buildQuery, page, seed } from '@albatrosdeveloper/ave-utils-npm/lib/utils/query.util';
import { OrderAttributes } from '@albatrosdeveloper/ave-models-package';
import { takeUntil } from 'rxjs';
import get from 'lodash-es/get';
import ceil from 'lodash-es/ceil';
import { OrderCustomerLogService } from '../order-customer-log.service';
import { LOCALIZED_PROPERTIES, ORDER_CUSTOMER_LOG_EXPORT_ID } from './order-customer-log.constants';
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';

@Injectable()
export class OrderCustomerLogExport {
  constructor(
    private exportService: ExportService,
    private orderCustomer: OrderCustomerLogService,
  ) { }

  private transformItem(item: OrderAttributes) {
    const positionsToFind = get(item, 'detailsPositionsUpdated', []);
    const allDetailsAfter = get(item, 'documentAfterUpdate.detail', []);
    const allDetailsBefore = get(item, 'documentBeforeUpdate.detail', []);

    return positionsToFind.map((position: number) => {
      const detailAfter = allDetailsAfter.find((d: any) => get(d, 'position') === position);
      const detailBefore = allDetailsBefore.find((d: any) => get(d, 'position') === position);

      const currentDetail = detailAfter || detailBefore;

      // Detectar si fue eliminada (statusId === INACTIVE en after)
      const isDeleted = detailAfter?.statusId === StatusEnum.INACTIVE;

      return {
        // Campos generales
        code: get(item, 'documentAfterUpdate.subRoute.externalKeys.code', '??'),
        subRoute: get(item, 'documentAfterUpdate.subRoute.name', '??'),
        user: get(item, 'documentAfterUpdate.user.user_login.username', '??'),
        serial: get(item, 'documentAfterUpdate.serial', '??'),
        date: ExportTaskProcessor.types.excel.datetime(get(item, 'documentAfterUpdate.creationDateTime', null)),
        shipToExternalKey: get(item, 'documentAfterUpdate.shipTo.externalKeys.code', '??'),
        shipToName: get(item, 'documentAfterUpdate.shipTo.name', '??'),

        // Material (siempre único)
        materialCode: get(currentDetail, 'material.externalKeys.code', '??'),
        materialName: get(currentDetail, 'material.name', '??'),

        // Cantidades (before/after)
        soldQuantityBefore: detailBefore ? get(detailBefore, 'soldQuantity', '??') : '??',
        soldQuantity: detailAfter ? get(detailAfter, 'soldQuantity', '??') : '??',

        baseUnitOfMeasureCode: `${get(currentDetail, 'baseUnitOfMeasure.code', '??')} ${get(currentDetail, 'baseUnitOfMeasure.name', '??')}`,

        pararellQuantityBefore: detailBefore ? get(detailBefore, 'pararellQuantity', '??') : '??',
        pararellQuantity: detailAfter ? get(detailAfter, 'pararellQuantity', '??') : '??',

        pararellUnitOfMeasureCode: `${get(currentDetail, 'pararellUnitOfMeasure.code', '??')} ${get(currentDetail, 'pararellUnitOfMeasure.name', '??')}`,

        // Precios y totales (before/after)
        unitPriceBefore: ExportTaskProcessor.types.number(detailBefore ? get(detailBefore, 'unitPrice', '??') : '??'),
        unitPrice: ExportTaskProcessor.types.number(detailAfter ? get(detailAfter, 'unitPrice', '??') : '??'),

        subTotalBefore: ExportTaskProcessor.types.number(detailBefore ? get(detailBefore, 'subTotal', '??') : '??'),
        subTotal: ExportTaskProcessor.types.number(detailAfter ? get(detailAfter, 'subTotal', '??') : '??'),

        totalBefore: ExportTaskProcessor.types.number(detailBefore ? get(detailBefore, 'total', '??') : '??'),
        total: ExportTaskProcessor.types.number(detailAfter ? get(detailAfter, 'total', '??') : '??'),

        // NUEVO: Indicador de eliminado (ÚLTIMA columna)
        deleted: isDeleted ? 'Sí' : 'No'
      };
    });
  }

  export(exportFormat: ExportFileFormat, range: ExportRange, totalItems: number, pageSize: number, pageIndex: number,
    id: string, title: string) {
    const totalPages = ceil(totalItems / pageSize) + 1;
    this.exportService.task({
      id: id,
      title: title,
      format: exportFormat,
      iterations: range === ExportRange.AllPages ? totalPages : 1,
      start: (ctx: ExportContext) => {
        ctx.processor.localizeItemProperties(title, LOCALIZED_PROPERTIES);
      },
      next: (ctx: ExportContext) => {
        const currentPage = range === ExportRange.AllPages ? ctx.iteration : pageIndex;
        this.orderCustomer.findByFilter(buildQuery(
          seed(this.orderCustomer.currentFilter), page(currentPage, pageSize)
        ), { contextHeader: 'export' }).pipe(takeUntil(ctx.stopped)).subscribe((response) => {
          get(response, 'data.0.data', []).map((item) => {
            if (exportFormat === ExportFileFormat.Excel) {
              ctx.processor.addToSection(title, this.transformItem(item));
            } else {
              ctx.processor.addToSection(title, this.transformItem(item));
            }
          });
          ctx.next();
        });
      },
    }).start();
  }

  canStartTask() {
    if (this.exportService.taskIsActive(ORDER_CUSTOMER_LOG_EXPORT_ID)) {
      this.exportService.setVisible(true);
      return false;
    }
    return true;
  }

}
